/**
 * The bar for a selection (`equipment-page-table`, R13; frame 07, pin 2): *{n} selected*, the bulk
 * actions' slot (`equipment-page-bulk-actions` fills it), *Remove*, and an icon-only clear.
 *
 * A page composition of the design system's `Card` and `Button`s, floating centred at the foot of
 * the main panel while the selection is non-empty, in both views (overlaid; see `Anchor`). Escape inside it clears the
 * selection. *Remove* sits after the slot (Human Action 2, reading 1).
 *
 * Plain `styled.div`s with the design system's parts rendered INSIDE them, never `styled(Card)` at
 * module scope — the wizard's page suites mock `@repo/design-system` part by part (see
 * `EquipmentStage`).
 */
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button, Card, Icon } from '@repo/design-system';
import { ink, space, typeRole, weight } from '@repo/design-tokens';
import { visuallyHidden } from '@repo/ui';

import { COUNT_ANNOUNCE_DELAY_MS } from './EquipmentStage';

export interface EquipmentSelectionBarProps {
  count: number;
  /** The bulk actions (`EquipmentBulkActions`, `equipment-page-bulk-actions`), after the count. */
  actions?: React.ReactNode;
  /** A bulk action is saving (`equipment-page-bulk-actions`, R1, R9): *Remove* waits for it. */
  busy?: boolean;
  onRemove: () => void;
  /** False while the plan is not loaded: *Remove* is unavailable and says why (R14). */
  removeAvailable: boolean;
  onClear: () => void;
}

/**
 * Overlaid at the foot of the main panel, centred, and never in the layout: a zero-height anchor
 * sticks to the panel's visible foot (the panel scrolls, so an absolutely placed bar on the panel
 * would scroll away), and the bar is placed above it. Appearing therefore re-lays out nothing — at
 * 2,000 rows an in-flow bar resized the table and its first tick cost a 304 ms task (NFR1). The
 * space it covers is reserved by the hosts: the Rows view's foot, and the Groups view's padding.
 */
const Anchor = styled.div`
  position: sticky;
  bottom: 0;
  height: 0;
  z-index: 3;
  align-self: stretch;
`;

const Float = styled.div`
  position: absolute;
  left: 50%;
  bottom: 0;
  transform: translateX(-50%);
  /*
   * Its own width, not the shrink-to-fit width \`left: 50%\` leaves it (half the panel), which wrapped
   * the bulk actions' labels onto two and three lines (\`equipment-page-bulk-actions\`, found in the
   * real-app check). Frame 07 draws the bar on one line.
   */
  width: max-content;
  max-width: 100%;

  .bar-row {
    display: flex;
    align-items: center;
    gap: ${space[2]};
    white-space: nowrap;
  }
  .bar-row > * {
    flex: none;
  }
  .bar-count {
    padding: 0 ${space[2]};
    font-size: ${typeRole.body1.fontSize};
    line-height: ${typeRole.body1.lineHeight};
    font-weight: ${weight.semibold};
    color: ${ink[1]};
    white-space: nowrap;
  }
`;

/** The height a host reserves for the bar at its foot, so the bar covers nothing it needs. */
export const SELECTION_BAR_CLEARANCE = `calc(${space[12]} + ${space[3]})`;

export const selectedText = (count: number): string => `${count} selected`;

/**
 * Clearing from the bar hides the bar, which holds focus: hand focus to the main panel's title, as
 * a removal does (A2), rather than letting it fall to the page.
 */
const clearAndKeepFocus = (onClear: () => void) => {
  onClear();
  window.setTimeout(() => document.getElementById('equipment-main-title')?.focus(), 0);
};

export const EquipmentSelectionBar: React.FC<EquipmentSelectionBarProps> = ({
  count,
  actions,
  onRemove,
  removeAvailable,
  onClear,
  busy = false,
}) => {
  // Said after the ticking pauses, as the count line is, rather than on every Shift-click.
  const [announced, setAnnounced] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(
      () => setAnnounced(count > 0 ? selectedText(count) : ''),
      COUNT_ANNOUNCE_DELAY_MS,
    );
    return () => window.clearTimeout(timer);
  }, [count]);

  // A write can empty the selection (its rows leave the chip or search shown), which hides the bar
  // and the control that held focus (`equipment-page-bulk-actions`, criterion 13). Focus left in the
  // bar, or dropped to the page by a closing popover, goes to the main panel's title, as a clear
  // does. After the popover's own restore has run; focus on a real control elsewhere stays.
  const anchorRef = useRef<HTMLDivElement>(null);
  const previousCount = useRef(count);
  useEffect(() => {
    const emptied = previousCount.current > 0 && count === 0;
    previousCount.current = count;
    if (!emptied) return;
    const timer = window.setTimeout(() => {
      const active = document.activeElement;
      if (!active || active === document.body || anchorRef.current?.contains(active)) {
        document.getElementById('equipment-main-title')?.focus();
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [count]);

  return (
    <>
      <span role="status" aria-live="polite" style={visuallyHidden}>
        {announced}
      </span>
      {/*
       * Mounted while empty, and hidden: its styles are then injected with the table's first
       * render, not on the first tick. Injecting a component's styles makes the browser restyle
       * the whole document — at 2,000 rows that was a 234 ms style recalculation on the first
       * tick (traced, NFR1).
       */}
      <Anchor ref={anchorRef} hidden={count === 0}>
        <Float
          data-testid="equipment-selection-bar"
          onKeyDown={event => {
            if (event.key !== 'Escape') return;
            event.stopPropagation();
            clearAndKeepFocus(onClear);
          }}
        >
          <Card surface="on-glass" size="sm" role="group" aria-label="Selected assets">
            <div className="bar-row">
              <span className="bar-count">{selectedText(count)}</span>
              {actions}
              <Button
                variant="text"
                tone="destructive"
                size="sm"
                iconLeft={<Icon name="trash-can" size="sm" />}
                aria-disabled={!removeAvailable || busy}
                aria-label={
                  !removeAvailable
                    ? 'Remove — unavailable until the plan has loaded'
                    : busy
                      ? 'Remove — unavailable while a change is saving'
                      : `Remove ${selectedText(count)}`
                }
                onClick={() => {
                  if (removeAvailable && !busy) onRemove();
                }}
              >
                Remove
              </Button>
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                aria-label="Clear selection"
                iconLeft={<Icon name="xmark" size="sm" />}
                onClick={() => clearAndKeepFocus(onClear)}
              />
            </div>
          </Card>
        </Float>
      </Anchor>
    </>
  );
};

export default EquipmentSelectionBar;
